import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Copy, ExternalLink, Loader2, Palette, RefreshCw, Star } from 'lucide-react';
import { hotelInfo } from '../../../data/mock';
import { apiCall } from '../_shared';
import { useSetting } from './useSetting';

const LANG_NAMES = { en: 'English', de: 'Deutsch', pl: 'Polski', ru: 'Русский', sr: 'Crnogorski / Srpski', sq: 'Shqip', hr: 'Hrvatski', hu: 'Magyar', it: 'Italiano', cs: 'Čeština', sk: 'Slovenčina' };
const CSS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/beds24-booking.css`;
const BEDS24_LANG = { en: 'en', de: 'de', pl: 'pl', ru: 'ru', sr: 'sr', hr: 'hr', hu: 'hu', it: 'it', cs: 'cs', sk: 'sk' };

// Live Beds24 checkout rendered with the UNSAVED texts: each edit → 1 h draft token → Beds24 reloads the stylesheet from ?draft=token.
const CheckoutPreview = ({ texts }) => {
  const [lang, setLang] = useState('en');
  const [cssPath, setCssPath] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setBusy(true);
      apiCall('/api/admin/rates/settings/booking_page/preview', { method: 'POST', body: JSON.stringify({ texts }) })
        .then((d) => setCssPath(d.css_path)).catch(() => {}).finally(() => setBusy(false));
    }, 900);
    return () => clearTimeout(timer.current);
  }, [texts]);
  const stay = new Date(Date.now() + 45 * 86400000).toISOString().slice(0, 10);
  const out = new Date(Date.now() + 47 * 86400000).toISOString().slice(0, 10);
  const css = cssPath ? `${process.env.REACT_APP_BACKEND_URL}${cssPath}` : CSS_URL;
  const src = `https://www.beds24.com/booking2.php?propid=${hotelInfo.beds24Id}&hidealiases=1&hidehead=1&hidefooter=1&hidepropdesc=1&hidepropname=1&checkin=${stay}&checkout=${out}&numadult=2&lang=${BEDS24_LANG[lang] || 'en'}&cssfile=${encodeURIComponent(css)}`;
  const langOpts = Object.keys(BEDS24_LANG).map((l) => <option key={l} value={l}>{LANG_NAMES[l] || l}</option>);
  return (
    <div className="bg-white rounded-xl border overflow-hidden sticky top-4" data-testid="bpt-preview">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 text-xs">
        <span className="font-semibold text-slate-800 inline-flex items-center gap-2">Checkout preview {busy && <Loader2 className="w-3 h-3 animate-spin text-[#0071c2]" />}</span>
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="border rounded px-1.5 py-1 bg-white" data-testid="bpt-preview-lang">{langOpts}</select>
      </div>
      {css.startsWith('https://') ? (
        <iframe key={src} src={src} title="Beds24 preview" className="w-full border-0" style={{ height: 640 }} data-testid="bpt-preview-iframe" />
      ) : <p className="p-4 text-xs text-amber-700" data-testid="bpt-preview-https">Preview needs an HTTPS app URL (Beds24 only loads HTTPS stylesheets).</p>}
      <p className="px-3 py-2 text-[11px] text-slate-500">Shows your unsaved edits as guests will see them (room list for a sample stay in 45 days). Save to publish.</p>
    </div>
  );
};

// One editable text: English + optional translations (empty = the wording built into the base stylesheet stays).
const FieldRow = ({ field, value, langs, onChange }) => {
  const [open, setOpen] = useState(false);
  const v = value || {};
  const multi = field.key.startsWith('quote') || field.key === 'guest_strip';
  const Input = multi ? 'textarea' : 'input';
  const langList = langs;
  const others = langList.filter((l) => l !== 'en');
  const filled = others.filter((l) => v[l]).length;
  const rows = others.map((l) => (
    <label key={l} className="block text-[11px]"><span className="text-slate-500">{LANG_NAMES[l] || l}</span>
      <Input rows={2} value={v[l] || ''} onChange={(e) => onChange({ ...v, [l]: e.target.value })} className="mt-0.5 w-full border rounded px-2 py-1 text-xs" data-testid={`bpt-${field.key}-${l}`} />
    </label>
  ));
  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={`bpt-field-${field.key}`}>
      <label className="block text-xs"><span className="font-semibold text-slate-800">{field.label}</span>
        <Input rows={2} value={v.en || ''} onChange={(e) => onChange({ ...v, en: e.target.value })} placeholder="Leave empty to keep the built-in text" className="mt-1 w-full border rounded px-2 py-1.5 text-sm" data-testid={`bpt-${field.key}-en`} />
      </label>
      <button type="button" onClick={() => setOpen(!open)} className="mt-1.5 text-[11px] text-[#0071c2] inline-flex items-center gap-1" data-testid={`bpt-${field.key}-langs`}>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Translations{filled ? ` (${filled} filled)` : ''}
      </button>
      {open && <div className="grid sm:grid-cols-2 gap-2 mt-2">{rows}</div>}
    </div>
  );
};

const HEAD_SNIPPET = `<link rel="stylesheet" href="${CSS_URL}">\n<script src="${process.env.REACT_APP_BACKEND_URL}/beds24-lightbox.js" defer></script>`;

const StatusLine = ({ ok, pending, children, testId }) => (
  <p className="font-semibold inline-flex items-center gap-1.5" data-testid={testId}>
    {pending ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
    {children}
  </p>
);

// Two one-minute jobs inside Beds24 → Developers: clear the old pasted Custom CSS, paste the picture pop-up script. Verified live.
const Beds24Setup = () => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const check = async () => {
    setBusy(true); setErr('');
    try { setRes(await apiCall('/api/admin/rates/settings/booking_page/beds24-css-check')); } catch (e) { setErr(e.message || 'Check failed'); } finally { setBusy(false); }
  };
  useEffect(() => { check(); }, []);
  const copy = () => { navigator.clipboard?.writeText(HEAD_SNIPPET).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {}); };
  const cssClean = !!res && !res.pasted_css;
  const lbOk = !!res && res.lightbox_installed && res.stylesheet_linked;
  const allOk = cssClean && lbOk;
  const missing = res ? [!res.stylesheet_linked && 'live stylesheet', !res.lightbox_installed && 'picture pop-up'].filter(Boolean).join(' + ') : '';
  return (
    <div className={`rounded-xl border p-4 ${allOk ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="bpt-beds24-cleanup">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-xs text-slate-800 space-y-2 min-w-0">
          <p className="text-sm font-bold text-slate-900">Beds24 settings — open <a href="https://beds24.com/control3.php" target="_blank" rel="noreferrer" className="underline text-[#0071c2]" data-testid="bpt-beds24-link">Beds24</a> → <b>SETTINGS → BOOKING ENGINE → PROPERTY BOOKING PAGE → DEVELOPERS</b></p>
          <div>
            <StatusLine ok={cssClean} pending={!res && !err} testId="bpt-beds24-css-status">
              1 · Custom CSS box: {res ? (cssClean ? 'empty — only the live stylesheet applies' : `old pasted CSS still present (${res.rules} rules${res.stale_pill ? ', includes the stale "8.0 · 590 reviews" pill' : ''})`) : 'checking…'}
            </StatusLine>
            {res && !cssClean && <p className="ml-5 mt-0.5" data-testid="bpt-beds24-steps">Delete everything in the <b>Custom CSS</b> box and <b>Save</b> (also under <b>MULTI BOOKING PAGE → DEVELOPERS</b> if pasted there). Until then the live stylesheet still wins — it loads after it and uses <code>!important</code>.</p>}
          </div>
          <div>
            <StatusLine ok={lbOk} pending={!res && !err} testId="bpt-beds24-lightbox-status">
              2 · HTML head (live stylesheet on every Beds24 link + picture pop-up): {res ? (lbOk ? 'installed' : `missing: ${missing}`) : 'checking…'}
            </StatusLine>
            {res && !lbOk && (
              <div className="ml-5 mt-1 space-y-1" data-testid="bpt-lightbox-steps">
                <p>Paste these two lines into <b>Insert in HTML &lt;HEAD&gt; bottom</b> and <b>Save</b> (not the "Confirmation Page" box). Line 1 brands even Beds24 links that bypass your website and always shows the live score and quotes; line 2 opens room pictures in a pop-up. Do not paste the CSS itself into the Custom CSS box — it would go stale again.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="block bg-white border rounded px-2 py-1 text-[11px] break-all whitespace-pre-wrap" data-testid="bpt-lightbox-snippet">{HEAD_SNIPPET}</code>
                  <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded border bg-white px-2 py-1 text-[11px] font-semibold" data-testid="bpt-lightbox-copy"><Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy'}</button>
                </div>
                <p className="text-slate-500">Guests then click any room photo → full-size gallery pop-up with ‹ › arrows, swipe on phones, Esc / × to close. <a href={`${process.env.REACT_APP_BACKEND_URL}/beds24-lightbox.js`} target="_blank" rel="noreferrer" className="underline" data-testid="bpt-lightbox-link">View script</a></p>
              </div>
            )}
          </div>
        </div>
        <button type="button" onClick={check} disabled={busy} className="inline-flex items-center gap-1.5 rounded border bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 disabled:opacity-50" data-testid="bpt-beds24-check">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Check again
        </button>
      </div>
      {err && <p className="text-xs text-red-700 mt-2" data-testid="bpt-beds24-error">{err}</p>}
    </div>
  );
};

// Header shown by beds24-lightbox.js on stand-alone Beds24 pages — phone, email, socials, back link; preview mirrors the real one.
const HeaderSettings = ({ header, labels, langs, onChange }) => {
  const h = header;
  const [openLangs, setOpenLangs] = useState(false);
  const keys = Object.keys(labels);
  const pl = h.place_langs || {};
  const others = (langs || []).filter((l) => l !== 'en');
  const filled = others.filter((l) => pl[l]).length;
  const placeRows = others.map((l) => (
    <label key={l} className="block text-[11px]"><span className="text-slate-500">{LANG_NAMES[l] || l}</span>
      <input value={pl[l] || ''} onChange={(e) => onChange({ ...h, place_langs: { ...pl, [l]: e.target.value } })} className="mt-0.5 w-full border rounded px-2 py-1 text-xs" data-testid={`bpt-header-place-${l}`} />
    </label>
  ));
  const inputs = keys.map((k) => (
    <label key={k} className="block text-[11px]"><span className="text-slate-500">{labels[k]}</span>
      <input value={h[k] || ''} onChange={(e) => onChange({ ...h, [k]: e.target.value })} className="mt-0.5 w-full border rounded px-2 py-1 text-xs" data-testid={`bpt-header-${k}`} />
      {k === 'place' && (
        <button type="button" onClick={() => setOpenLangs(!openLangs)} className="mt-1 text-[11px] text-[#0071c2] inline-flex items-center gap-1" data-testid="bpt-header-place-langs">
          {openLangs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Translations{filled ? ` (${filled} filled)` : ''} — shown in the guest's language on Beds24
        </button>
      )}
    </label>
  ));
  const stars = '★'.repeat(Number(h.stars) || 0);
  return (
    <div className="bg-white rounded-xl border p-4" data-testid="bpt-header-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-bold text-slate-900">Header on the Beds24 page</h4>
        <label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={h.show !== false} onChange={(e) => onChange({ ...h, show: e.target.checked })} data-testid="bpt-header-show" /> Show header</label>
      </div>
      <p className="text-[11px] text-slate-500 mt-1">Appears when a guest opens the Beds24 booking page directly (Beds24 link, Google, old e-mails). Inside your website the site header is used, so nothing doubles. Changes reach Beds24 within 5 minutes.</p>
      <div className="mt-3 rounded-lg overflow-hidden border text-[11px]" data-testid="bpt-header-preview" style={{ opacity: h.show === false ? 0.4 : 1 }}>
        <div className="bg-[#1a1f2e] text-white px-3 py-1.5 flex justify-between gap-2"><span className="truncate">☎ {h.phone} &nbsp; ✉ {h.email}</span><span className="whitespace-nowrap">{[h.whatsapp && 'WhatsApp', h.facebook && 'Facebook', h.instagram && 'Instagram', h.tiktok && 'TikTok'].filter(Boolean).join(' · ')}</span></div>
        <div className="bg-white px-3 py-2 flex items-center justify-between gap-2"><span className="inline-flex items-center gap-2"><img src="/logo.png" alt="" className="w-7 h-7 object-contain" /><span><b className="block text-xs text-slate-900">{h.name}</b><span className="text-slate-500"><span className="text-orange-500">{stars}</span> {h.place}</span></span></span><span className="bg-orange-500 text-white font-bold rounded-full px-3 py-1 whitespace-nowrap">← Back to website</span></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        <label className="block text-[11px]"><span className="text-slate-500">Stars</span>
          <select value={h.stars ?? 4} onChange={(e) => onChange({ ...h, stars: Number(e.target.value) })} className="mt-0.5 w-full border rounded px-2 py-1 text-xs bg-white" data-testid="bpt-header-stars">
            <option value={0}>none</option><option value={3}>3</option><option value={4}>4</option><option value={5}>5</option>
          </select>
        </label>
        {inputs}
      </div>
      {openLangs && <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2 border-t pt-2" data-testid="bpt-header-place-translations">{placeRows}</div>}
    </div>
  );
};

export const BookingPageTexts = () => {
  const { data, saving, err: error, save } = useSetting('booking_page');
  const [meta, setMeta] = useState({ fields: [], langs: ['en'], header_labels: {}, header_defaults: {} });
  const [texts, setTexts] = useState(null);
  const [header, setHeader] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => { apiCall('/api/public/beds24-booking-fields').then(setMeta).catch(() => {}); }, []);
  useEffect(() => { if (data && texts === null) { setTexts(data.texts || {}); setHeader(data.header || null); } }, [data, texts]);
  if (texts === null) return <div className="bg-white rounded-xl border p-6 text-sm text-slate-500" data-testid="bpt-loading">Loading booking-page texts…</div>;
  const hdr = header || meta.header_defaults || {};

  const onSave = async () => {
    const clean = {};
    Object.entries(texts).forEach(([k, per]) => { const o = {}; Object.entries(per || {}).forEach(([l, t]) => { if ((t || '').trim()) o[l] = t.trim(); }); if (Object.keys(o).length) clean[k] = o; });
    const ok = await save({ texts: clean, header: hdr });
    if (ok) { setTexts(clean); setMsg('Saved — the Beds24 page picks it up within 5 minutes (or reload the booking page).'); setTimeout(() => setMsg(''), 5000); }
  };
  const fieldList = meta.fields;
  const fieldRows = fieldList.map((f) => <FieldRow key={f.key} field={f} value={texts[f.key]} langs={meta.langs} onChange={(v) => setTexts({ ...texts, [f.key]: v })} />);
  const rv = meta.review;
  const quoteList = meta.quotes || [];
  const quoteItems = quoteList.slice(0, 4).map((q) => (
    <li key={q.id} className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700" data-testid={`bpt-quote-${q.id}`}>
      <span className="font-semibold text-[#003b95]">{q.score}/10</span> · {q.name}{q.country ? ` (${q.country})` : ''} · {q.date} — <i>{(q.title || q.positive || '').slice(0, 90)}</i>
    </li>
  ));

  return (
    <div className="space-y-4" data-testid="booking-page-texts">
      <div className="bg-white rounded-xl border p-5">
        <h3 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2"><Palette className="w-5 h-5 text-[#0071c2]" /> Booking page texts</h3>
        <p className="text-sm text-slate-600 mt-1">The banner, badges, guest quotes and confirmation texts shown inside the Beds24 checkout on your website. Edit here — no CSS, no Beds24 settings. Empty fields keep the built-in wording. The early-booker ribbon follows your active <b>Early booker</b> promotion automatically (discount % and end date); fill the ribbon field only to override it.</p>
        <p className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Stylesheet served at <a href={CSS_URL} target="_blank" rel="noreferrer" className="underline" data-testid="bpt-css-link">{CSS_URL}</a></p>
        <p className="text-xs mt-2 inline-flex items-center gap-2 rounded-full border border-[#003b95]/30 bg-[#003b95]/5 px-3 py-1" data-testid="bpt-live-review">
          <Star className="w-3.5 h-3.5 text-[#003b95]" />
          {rv ? <span><b>Live Booking.com score:</b> {meta.review_text} <span className="text-slate-500">· read {String(rv.at || '').slice(0, 10)}, refreshed nightly by the rate sync check</span></span>
              : <span className="text-slate-600">Live Booking.com score not read yet — run <b>Rate sync check → Check now</b>; until then the built-in pill text is shown.</span>}
        </p>
        {meta.quotes?.length > 0 && (
          <div className="mt-2 text-xs" data-testid="bpt-auto-quotes">
            <p className="font-semibold text-slate-800">Latest 9+ Booking.com reviews (fill the quote cards automatically, refreshed nightly):</p>
            <ul className="mt-1 grid md:grid-cols-2 gap-1">{quoteItems}</ul>
          </div>
        )}
      </div>
      <Beds24Setup />
      <HeaderSettings header={hdr} labels={meta.header_labels || {}} langs={meta.langs} onChange={setHeader} />
      <div className="grid lg:grid-cols-[1fr_440px] gap-4 items-start">
        <div className="grid md:grid-cols-2 gap-3">{fieldRows}</div>
        <CheckoutPreview texts={texts} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onSave} disabled={saving} className="bg-[#0071c2] text-white font-semibold rounded px-5 py-2 text-sm disabled:opacity-50" data-testid="bpt-save">{saving ? 'Saving…' : 'Save texts'}</button>
        {error && <span className="text-xs text-red-700" data-testid="bpt-error">{error}</span>}
        {msg && <span className="text-xs text-emerald-700" data-testid="bpt-saved">{msg}</span>}
      </div>
    </div>
  );
};
